import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import * as Sentry from '@sentry/react';
import {ArrowRight, BellSlash, Robot} from '../../icons';
import {Spinner} from '../../Spinner';
import {AlertsApi} from '../../../api/alertsApi';
import {
    alertTitle,
    alertTone,
    getIndicatorConfig,
    itemSeverityLevel,
} from '../../client/activity_log/detail_renderers/alert_indicators';
import {AlertSnoozeModal, copyAlertPrompt, useAlertSnooze} from './alertSnooze';
import {
    alertLinesStillText,
    alertLinesText,
    alertPartialSnoozeText,
} from '../../alerts_center/shared/alertLines';

const SEVERITY_BY_LEVEL = {CRITIC: 'critic', BAD: 'bad'};

// La gravedad de la fila sale de `itemSeverityLevel`, la misma que cuenta el chip «Críticas»: el ODS no
// trae una gravedad en `level` (trae la comparación), pero se emite sólo al entrar en crítico.
export function severityOf(item) {
    return SEVERITY_BY_LEVEL[itemSeverityLevel(item)] || null;
}

// Primer día de la ventana de vigencia, en fecha LOCAL (`toISOString()` la pasaría a UTC y en
// Argentina eso corre el día uno para atrás). Mismo formato que espera el deep-link a la Bitácora.
export function windowStartDate(windowDays) {
    const start = new Date();
    start.setDate(start.getDate() - (windowDays - 1));
    const month = String(start.getMonth() + 1).padStart(2, '0');
    const day = String(start.getDate()).padStart(2, '0');
    return `${start.getFullYear()}-${month}-${day}`;
}

// Qué pasó, con la métrica y el desvío: el MISMO título que la Bitácora le da a esa alerta
// («Conversiones cayó 60% vs. lo esperado»), armado con su config e i18n a partir del `detail`
// que manda el backend. Si esa config no sabe titularla (un indicador sin entrada, un backend
// viejo sin `detail`, datos incompletos), cae al `label` del backend, que ya nombra la métrica
// cuando puede. Card C0LDeNRi: «una métrica se salió de rango» no decía cuál.
export function alertItemTitle(item, t) {
    const detail = displayDetail(item);
    const title = detail ? alertTitle({detail}, t, {inTray: true}) : null;
    return title || item.label;
}

const CUSTOM_METRIC = /^CUSTOM:\d+$/;

// Una métrica personalizada se nombra con el `metric_label` que ya resolvió el backend (scopeado a
// la agencia). El cache de nombres del FE lo llena un fetch de la pantalla que puede no haber
// terminado —o haber fallado— cuando se abre el popover, y ahí el título decía «Métrica
// personalizada cayó…» con el nombre real en el mismo payload.
function displayDetail(item) {
    const detail = item.detail;
    if (!detail || !CUSTOM_METRIC.test(detail.metric_name || '') || !item.metric_label) return detail;
    return {...detail, metric_name: item.metric_label};
}

// Los números de la alerta («Desvío ↓ 60%», «Acumulado 12», «Esperado a hoy 30», «Umbral 20%»),
// con los mismos stats que la Bitácora. Un stat sin valor se omite; sin config o sin datos, lista
// vacía y la línea no se dibuja. Devuelve cada stat por separado para que la pantalla los dibuje
// como bloques que no se parten: unidos en un string, el «$» quedaba al final de un renglón y el
// número en el siguiente.
export function alertItemFacts(item, t) {
    const detail = displayDetail(item);
    const config = detail ? getIndicatorConfig(detail.indicator) : null;
    if (!config || !config.stats) return [];
    let stats;
    try {
        stats = config.stats(detail, t, alertTone(detail));
    } catch (e) {
        // La alerta se sigue mostrando (con su título), pero sin números no puede quedar en
        // silencio: la config es la misma de la Bitácora, y un bug ahí vacía las dos pantallas.
        Sentry.captureException(e, {extra: {scope: 'alerts_popover_facts', indicator: detail.indicator}});
        return [];
    }
    return (stats || []).filter((stat) => stat.value !== null && stat.value !== undefined)
        .map((stat) => `${stat.label} ${stat.value}`);
}

export function formatDate(isoDate, language) {
    if (!isoDate) return null;
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString(language || undefined, {day: 'numeric', month: 'long'});
}

/**
 * El detalle de las alertas vigentes de un cliente: qué está roto AHORA, desde cuándo y qué hacer.
 *
 * Renderiza sólo el panel — el trigger (la pill) y el cierre por click afuera los maneja la fila,
 * cuyo ref tiene que envolver a los dos. Los datos se piden al abrir, no antes: la pill ya trae el
 * número, y una lista que casi nadie despliega no se paga en cada carga del listado.
 *
 * Las acciones NO tienen endpoints propios: cada ítem viaja con las coordenadas (`snooze`) de los
 * endpoints de silenciado que ya existen, así que "Silenciar todas" son N llamadas a esos — y NO
 * `alert-snooze-all`, que apaga TODOS los indicadores de una línea (otra cosa).
 */
export function AlertsPopover(props) {
    const {clientId, clientName, isOpen, memoryEnabled, onCountsChange, onInteractionLock} = props;
    const {t, i18n} = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);
    const [data, setData] = useState(null);

    const publishCounts = useCallback((payload) => {
        if (!payload || !onCountsChange) return;
        onCountsChange({
            alerts_count: payload.count,
            alerts_snoozed_count: payload.snoozed_count,
            // El vencimiento más lejano de las silenciadas; `null` = alguna no vence nunca, que es
            // lo que la fila necesita para no prometer una fecha.
            alerts_snoozed_until: maxSnoozedUntil(payload.items),
        });
    }, [onCountsChange]);

    const load = useCallback(() => {
        setIsLoading(true);
        setHasFailed(false);
        return AlertsApi.getActiveAlerts(clientId).then((response) => {
            if (response && response.status === 'OK') {
                setData(response.data);
                publishCounts(response.data);
            } else {
                setHasFailed(true);
            }
        }).finally(() => setIsLoading(false));
    }, [clientId, publishCounts]);

    const snooze = useAlertSnooze({reload: load});
    const {snoozeTarget, openSnooze, reactivate} = snooze;

    useEffect(() => {
        if (isOpen) load();
    }, [isOpen, load]);

    // Mientras el modal está abierto, la fila no puede cerrar el popover: el modal se monta en un
    // portal fuera del ref, así que cualquier click adentro se lee como "click afuera".
    useEffect(() => {
        if (onInteractionLock) onInteractionLock(snoozeTarget !== null);
    }, [snoozeTarget, onInteractionLock]);

    if (!isOpen) return null;

    const items = (data && data.items) || [];
    const activeItems = items.filter((item) => !item.snoozed);
    const snoozedItems = items.filter((item) => item.snoozed);
    const snoozeableItems = activeItems.filter((item) => item.snooze && item.snooze.available);

    const renderMeta = (item) => {
        const parts = [];
        // Las líneas, con el mismo texto que la pantalla de Alertas y «Ahora» (`alertLines`).
        const lines = alertLinesText(item, t, i18n.language);
        if (lines) parts.push(lines);
        parts.push(t('clients_v2.alert_meta_started', {elapsed: elapsedSince(item.first_seen, t)}));
        const partial = alertPartialSnoozeText(item, t, i18n.language);
        // `occurrences` son DISPAROS, no chequeos: el backend escribe la fila sólo cuando la alerta
        // pasa a crítica desde un nivel no crítico (y ahí sale el mail). La primera también cuenta,
        // así que el viejo «volvió a aparecer N» estaba corrido en uno y además no se entendía.
        return (
            <div className="cv2-alertpop__meta">
                {parts.join(' · ')}
                {item.occurrences > 1 && (
                    <span className="cv2-alertpop__rep">
                        {` · ${t('clients_v2.alert_meta_fired', {count: item.occurrences})}`}
                    </span>
                )}
                {/* La línea ya silenciada de un grupo que sigue sonando se reactiva acá, como en la
                    pantalla de Alertas (`AlertItemRows`). */}
                {partial && (
                    <>
                        {` · ${partial} · `}
                        <button type="button" className="cv2-alertpop__link" disabled={snooze.isSaving}
                                onClick={() => reactivate(item)}>
                            {t('clients_v2.alert_reactivate')}
                        </button>
                    </>
                )}
            </div>
        );
    };

    const renderFacts = (item) => {
        const facts = alertItemFacts(item, t);
        if (!facts.length) return null;
        return (
            <div className="cv2-alertpop__facts">
                {/* El separador va AFUERA del bloque `nowrap`: es el único lugar donde la línea
                    puede partirse. */}
                {facts.map((fact, index) => (
                    <React.Fragment key={fact}>
                        {index > 0 && ' · '}
                        <span className="cv2-alertpop__fact">{fact}</span>
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const renderActiveItem = (item, index) => (
        <div className="cv2-alertpop__item" key={`active-${index}`}>
            <span className={`cv2-alertpop__dot${severityOf(item) ? ` -${severityOf(item)}` : ''}`}/>
            <div className="cv2-alertpop__body">
                {severityOf(item) && (
                    <div className={`cv2-alertpop__level -${severityOf(item)}`}>
                        {t(`clients_v2.alert_level_${severityOf(item)}`)}
                    </div>
                )}
                <div className="cv2-alertpop__title">{alertItemTitle(item, t)}</div>
                {renderFacts(item)}
                {renderMeta(item)}
                <div className="cv2-alertpop__acts">
                    {item.snooze && item.snooze.available && (
                        <button type="button" className="cv2-alertpop__btn"
                                onClick={() => openSnooze(item)}>
                            <BellSlash size={13} weight="bold"/>
                            {t('clients_v2.alert_snooze')}
                        </button>
                    )}
                    {item.prompt && (
                        <button type="button" className="cv2-alertpop__btn"
                                onClick={() => copyAlertPrompt(item, clientId, t)}>
                            <Robot size={13} weight="bold"/>
                            {t('clients_v2.alert_copy_prompt')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    const renderSnoozedItem = (item, index) => {
        const until = formatDate(item.snoozed_until, i18n.language);
        return (
            <div className="cv2-alertpop__item -snoozed" key={`snoozed-${index}`}>
                <span className="cv2-alertpop__dot"/>
                <div className="cv2-alertpop__body">
                    <div className="cv2-alertpop__title">{alertItemTitle(item, t)}</div>
                    <div className="cv2-alertpop__meta">
                        {alertLinesStillText(item, t, i18n.language)}
                    </div>
                    <span className="cv2-alertpop__silenced">
                        <BellSlash size={12} weight="bold"/>
                        {until
                            ? t('clients_v2.alert_snoozed_until', {date: until})
                            : t('clients_v2.alert_snoozed_forever')}
                        {item.snooze && item.snooze.available && (
                            <>
                                {' · '}
                                <button type="button" className="cv2-alertpop__link"
                                        onClick={() => reactivate(item)}>
                                    {t('clients_v2.alert_reactivate')}
                                </button>
                            </>
                        )}
                    </span>
                </div>
            </div>
        );
    };

    const renderBody = () => {
        if (isLoading && !data) {
            return <div className="cv2-alertpop__state"><Spinner color="positive small"/></div>;
        }
        if (hasFailed) {
            return <div className="cv2-alertpop__state">{t('clients_v2.alerts_load_error')}</div>;
        }
        return (
            <div className="cv2-alertpop__list">
                {activeItems.length === 0 && (
                    <div className="cv2-alertpop__state">{t('clients_v2.alerts_none_left')}</div>
                )}
                {activeItems.map(renderActiveItem)}
                {snoozedItems.length > 0 && (
                    <div className="cv2-alertpop__group">{t('clients_v2.alerts_snoozed_group')}</div>
                )}
                {snoozedItems.map(renderSnoozedItem)}
            </div>
        );
    };

    // La Bitácora arranca en el mismo día que la ventana de vigencia, así el usuario ve las ráfagas
    // de estos mismos problemas (más filas que el contador — ahí la unidad es cada vez que sonó).
    const bitacoraPath = memoryEnabled
        ? `/client/${clientId}/activity-log?layers=alerts`
          + `&date_from=${windowStartDate((data && data.window_days) || 7)}`
        : null;

    return (
        <div className="cv2-alertpop">
            <div className="cv2-alertpop__head">
                <span className="cv2-alertpop__t">{t('clients_v2.alerts_popover_title')}</span>
                {data && <span className="cv2-alertpop__count">{data.count}</span>}
                <span className="cv2-alertpop__spacer"/>
                {snoozeableItems.length > 0 && (
                    <button type="button" className="cv2-alertpop__btn"
                            onClick={() => openSnooze(null, snoozeableItems)}>
                        {t('clients_v2.alerts_snooze_all')}
                    </button>
                )}
            </div>

            {renderBody()}

            <div className="cv2-alertpop__foot">
                <span>
                    {snoozedItems.length > 0
                        ? t('clients_v2.alerts_foot_snoozed', {n: snoozedItems.length})
                        : t('clients_v2.alerts_foot')}
                </span>
                <span className="cv2-alertpop__spacer"/>
                {bitacoraPath && (
                    <Link className="cv2-alertpop__link" to={bitacoraPath}
                          onClick={(e) => e.stopPropagation()}>
                        {t('clients_v2.alerts_see_history')}
                        <ArrowRight size={12} weight="bold"/>
                    </Link>
                )}
            </div>

            <AlertSnoozeModal snooze={snooze} clientName={clientName}/>
        </div>
    );
}

// "hace 5 min" / "hace 3 d". Misma escala que la fila; el popover sólo necesita la parte corta.
export function elapsedSince(isoDate, t) {
    if (!isoDate) return t('clients_v2.elapsed_unknown');
    const elapsedMinutes = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
    if (!Number.isFinite(elapsedMinutes) || elapsedMinutes < 0) return t('clients_v2.elapsed_unknown');
    if (elapsedMinutes < 60) return t('clients_v2.elapsed_minutes', {n: Math.max(1, elapsedMinutes)});
    const elapsedHours = Math.floor(elapsedMinutes / 60);
    if (elapsedHours < 24) return t('clients_v2.elapsed_hours', {n: elapsedHours});
    const elapsedDays = Math.floor(elapsedHours / 24);
    if (elapsedDays < 60) return t('clients_v2.elapsed_days', {n: elapsedDays});
    const elapsedMonths = Math.floor(elapsedDays / 30);
    return elapsedMonths === 1
        ? t('clients_v2.elapsed_month_one')
        : t('clients_v2.elapsed_months', {n: elapsedMonths});
}

// El vencimiento más lejano de las silenciadas. `null` cuando alguna NO VENCE NUNCA: un silencio
// para siempre domina a cualquier fecha, y prometer "hasta el 8" escondería justo eso.
export function maxSnoozedUntil(items) {
    const snoozed = (items || []).filter((item) => item.snoozed);
    if (!snoozed.length || snoozed.some((item) => !item.snoozed_until)) return null;
    return snoozed.map((item) => item.snoozed_until).sort().pop();
}

export default AlertsPopover;
